import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useQuery } from '@apollo/client';
import { parseISO, isAfter, isBefore, endOfDay, startOfDay } from 'date-fns';
import { get as dig, omitBy, pick } from 'lodash';
import { useDispatch } from 'react-redux';

import {
  pageInfoFromData,
  edgesFromData,
  mergeNewPage,
  mergeEdges,
  edgesWithoutNodeIds,
  removeEdgesFromDataById,
} from 'helpers/graphql-utils';
import { isNullOrEmpty } from 'helpers/utils';
import { DESC } from 'constants/sortDirections';
import { FILTER_PHOTOS_ONLY, FILTER_VIDEOS_ONLY } from 'constants/filters';
import {
  ASSETS_DELETED,
  ASSETS_REASSIGNED,
  ASSET_DUPLICATED,
} from 'constants/graphqlEvents';
import { EVENT_ADD_WAITING_ASSET } from 'constants/events';
import eventEmitter from 'helpers/event-emitter';
import { photoProcessingCompleted } from 'reducers/photos';

import {
  GET_ASSET_FEED,
  ASSET_STATUS_CHANGED_SUBSCRIPTION,
  ASSET_DELETED_SUBSCRIPTION,
  ASSET_REASSIGNED_SUBSCRIPTION,
  ASSET_PROCESSED_SUBSCRIPTION,
} from './gql/AssetFeedQuery.graphql';

const ASSETS_PER_PAGE = 50;
const BASE_PATH = 'node.assetFeed';

const filterAssertionMap = {
  assetType: (assetType, assetItem) =>
    (assetType === FILTER_PHOTOS_ONLY && assetItem.__typename === 'Photo') ||
    (assetType === FILTER_VIDEOS_ONLY && assetItem.__typename === 'Video'),
  capturedAfter: (date, assetItem) => {
    return isAfter(parseISO(assetItem.capturedAt), date);
  },
  capturedBefore: (date, assetItem) => {
    return isBefore(parseISO(assetItem.capturedAt), date);
  },
  users: (users, assetItem) => {
    return users.includes(assetItem.contributor.id);
  },
  projects: (projects, assetItem) => {
    return projects.includes(assetItem.projectInfo.id);
  },
};

const nodeIdForEntity = (e) => e.node_id;

const convertFiltersToGraphQL = (filters) => {
  const variables = {};

  if (filters.assetType === FILTER_PHOTOS_ONLY) {
    variables.types = ['PHOTO'];
  } else if (filters.assetType === FILTER_VIDEOS_ONLY) {
    variables.types = ['VIDEO'];
  }

  if (filters.capturedAfter) {
    variables.capturedAfter = startOfDay(filters.capturedAfter);
  }
  if (filters.capturedBefore) {
    variables.capturedBefore = endOfDay(filters.capturedBefore);
  }

  if (filters.users?.length) {
    variables.users = filters.users.map(nodeIdForEntity);
  }
  if (filters.groups?.length) {
    variables.groups = filters.groups.map(nodeIdForEntity);
  }
  if (filters.projects?.length) {
    variables.projects = filters.projects.map(nodeIdForEntity);
  }
  if (filters.tags?.length) {
    variables.tags = filters.tags.map((tag) => tag.display_value);
  }

  return variables;
};

const sortEdgesByDate = (edges, desc) => {
  const sortedEdges = edges.sort((a, b) => {
    return parseISO(a.node.capturedAt) - parseISO(b.node.capturedAt);
  });

  return desc ? sortedEdges.reverse() : sortedEdges;
};

const assetItemFitsFilters = (assetItem, filters) => {
  const graphqlFilters = pick(filters, Object.keys(filterAssertionMap));
  const presentFilters = omitBy(graphqlFilters, isNullOrEmpty);

  return Object.keys(presentFilters).every((key) => {
    return filterAssertionMap[key](presentFilters[key], assetItem);
  });
};

const newEdgeForAssetItem = (assetItem) => {
  return {
    __typename: 'AssetItemEdge',
    node: assetItem,
  };
};

const mergeNewSubscriptionData = (oldData, { asset, variables }) => {
  if (!assetItemFitsFilters(asset, variables)) {
    return oldData;
  }

  return mergeEdges(
    oldData,
    sortEdgesByDate(
      [newEdgeForAssetItem(asset), ...edgesFromData(oldData, BASE_PATH)],
      variables.sortDirection === DESC,
    ),
    BASE_PATH,
  );
};

const removeAssetFromCache = (oldData, { subscriptionData }) => {
  if (!subscriptionData.data) {
    return oldData;
  }

  const edges = edgesWithoutNodeIds(
    edgesFromData(oldData, BASE_PATH),
    subscriptionData.data.assetDeleted.assetItem.id,
  );

  return mergeEdges(oldData, edges, BASE_PATH);
};

const handleAssetReassignedSubscriptionUpdate = (
  oldData,
  { subscriptionData, variables },
) => {
  if (!subscriptionData.data) {
    return oldData;
  }

  const { assetItem } = subscriptionData.data.assetReassigned;

  const parent = dig(oldData, 'node', {});
  const feedableType = parent.__typename;

  const projectId = assetItem.projectInfo.id;

  let edges = edgesFromData(oldData, BASE_PATH);

  if (feedableType === 'Project' && projectId !== parent.id) {
    edges = edgesWithoutNodeIds(edges, assetItem.id);
  } else {
    edges = assetItemFitsFilters(assetItem, variables)
      ? [newEdgeForAssetItem(assetItem), ...edges]
      : edges;
  }

  return mergeEdges(
    oldData,
    sortEdgesByDate(edges, variables.sortDirection === DESC),
    BASE_PATH,
  );
};

const ACTION_ADD_WAITING_ASSET = 'ACTION_ADD_WAITING_ASSET';
const ACTION_REMOVE_ASSET_FROM_WAITING = 'ACTION_REMOVE_ASSET_FROM_WAITING';

const waitingAssetsReducer = (state, action) => {
  switch (action.type) {
    case ACTION_ADD_WAITING_ASSET: {
      const { waitingAsset } = action;
      return [...state, waitingAsset];
    }
    case ACTION_REMOVE_ASSET_FROM_WAITING: {
      const { asset } = action;
      const incomingId = parseInt(asset.databaseId, 10);
      return state.filter(
        (waitingAsset) => waitingAsset.asset_v1_id !== incomingId,
      );
    }
    default:
      return state;
  }
};

export default function useAssetFeed({
  entityId,
  sortOrder,
  filters,
  tagsOperator,
}) {
  const [waitingAssets, dispatch] = useReducer(waitingAssetsReducer, []);
  const reduxDispatch = useDispatch();

  const queryVariables = useMemo(
    () => ({
      id: entityId,
      first: ASSETS_PER_PAGE,
      sortDirection: sortOrder,
      tagsOperator,
      ...convertFiltersToGraphQL(filters),
    }),
    [entityId, tagsOperator, filters, sortOrder],
  );

  const {
    loading,
    error,
    data: newData,
    previousData,
    fetchMore,
    subscribeToMore,
    updateQuery,
  } = useQuery(GET_ASSET_FEED, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const data = newData ?? previousData;

  const fetchNextPage = async () => {
    const pageInfo = pageInfoFromData(data, BASE_PATH);
    return fetchMore({
      variables: { ...queryVariables, after: pageInfo.endCursor },
      updateQuery: (oldData, result) => {
        return mergeNewPage(oldData, result, BASE_PATH);
      },
    });
  };

  const parent = dig(data, 'node', {});

  const assets = useMemo(
    () => edgesFromData(data, BASE_PATH).map((edge) => edge.node),
    [data],
  );

  useEffect(() => {
    const onAssetsDeleted = ({ deletedIds }) => {
      updateQuery((oldData) => {
        return removeEdgesFromDataById(oldData, BASE_PATH, deletedIds);
      });
    };
    eventEmitter.addListener(ASSETS_DELETED, onAssetsDeleted);

    return () => {
      eventEmitter.removeListener(ASSETS_DELETED, onAssetsDeleted);
    };
  }, [updateQuery]);

  useEffect(() => {
    const onAssetDuplicated = ({ assetItem }) => {
      updateQuery((oldData) => {
        return mergeEdges(
          oldData,
          sortEdgesByDate(
            [
              newEdgeForAssetItem(assetItem),
              ...edgesFromData(oldData, BASE_PATH),
            ],
            queryVariables.sortDirection === DESC,
          ),
          BASE_PATH,
        );
      });
    };
    eventEmitter.addListener(ASSET_DUPLICATED, onAssetDuplicated);

    return () => {
      eventEmitter.removeListener(ASSET_DUPLICATED, onAssetDuplicated);
    };
  }, [updateQuery, queryVariables]);

  useEffect(() => {
    const onAssetsReassigned = ({ assetItems }) => {
      updateQuery((oldData) => {
        let edges = edgesFromData(oldData, BASE_PATH);
        if (parent.__typename === 'Project') {
          const assetIdsToRemove = assetItems.reduce((memo, assetItem) => {
            if (assetItem.projectInfo.id !== parent.id) {
              memo.push(assetItem.id);
            }

            return memo;
          }, []);

          edges = edgesWithoutNodeIds(edges, assetIdsToRemove);
        }

        return mergeEdges(oldData, edges, BASE_PATH);
      });
    };
    eventEmitter.addListener(ASSETS_REASSIGNED, onAssetsReassigned);

    return () => {
      eventEmitter.removeListener(ASSETS_REASSIGNED, onAssetsReassigned);
    };
  }, [updateQuery, parent]);

  const removeAssetFromWaiting = useCallback((asset) => {
    dispatch({ type: ACTION_REMOVE_ASSET_FROM_WAITING, asset });
  }, []);

  useEffect(() => {
    const addWaitingAsset = (waitingAsset) => {
      dispatch({ type: ACTION_ADD_WAITING_ASSET, waitingAsset });
    };
    eventEmitter.addListener(EVENT_ADD_WAITING_ASSET, addWaitingAsset);
    return () => {
      eventEmitter.removeListener(EVENT_ADD_WAITING_ASSET, addWaitingAsset);
    };
  }, [dispatch]);

  useEffect(() => {
    const unsubscribe = subscribeToMore({
      document: ASSET_STATUS_CHANGED_SUBSCRIPTION,
      variables: { feedableId: entityId },
      updateQuery: (oldData, { subscriptionData, variables }) => {
        if (!subscriptionData.data) {
          return oldData;
        }
        const { assetItem: asset, status } =
          subscriptionData.data.assetStatusChanged;

        switch (status) {
          // when processed, remove from waiting and add asset to data
          case 'PROCESSED':
            removeAssetFromWaiting(asset);
            return mergeNewSubscriptionData(oldData, { asset, variables });
          // when processing did not occur (but was attempted), simply remove from waiting
          // note: could handle PROCESSING_ERROR better in future work?
          case 'DUPLICATE':
          case 'PROCESSING_ERROR':
            removeAssetFromWaiting(asset);
            return oldData;
          // when things are still waiting, just return old data!
          case 'PENDING':
          case 'PROCESSING':
          default:
            return oldData;
        }
      },
    });

    return unsubscribe;
  }, [entityId, subscribeToMore, removeAssetFromWaiting]);

  useEffect(() => {
    const unsubscribe = subscribeToMore({
      document: ASSET_DELETED_SUBSCRIPTION,
      variables: { feedableId: entityId },
      updateQuery: removeAssetFromCache,
    });

    return unsubscribe;
  }, [entityId, subscribeToMore]);

  useEffect(() => {
    const unsubscribe = subscribeToMore({
      document: ASSET_REASSIGNED_SUBSCRIPTION,
      variables: { feedableId: entityId },
      updateQuery: handleAssetReassignedSubscriptionUpdate,
    });

    return unsubscribe;
  }, [entityId, subscribeToMore]);

  useEffect(() => {
    const unsubscribe = subscribeToMore({
      document: ASSET_PROCESSED_SUBSCRIPTION,
      variables: { feedableId: entityId },
      updateQuery: (oldData, { subscriptionData, variables }) => {
        if (!subscriptionData.data) {
          return oldData;
        }
        const { assetItem: asset } = subscriptionData.data.assetProcessed;
        removeAssetFromWaiting(asset);
        // Use setTimeout to force a synchronous execution to ensure that the photoProcessingCompleted action is dispatched after the apollo cache is updated
        // Otherwise, the optimistic edit will be removed from the redux store before the cache is updated, and the old image will be displayed momentarily
        setTimeout(() => {
          reduxDispatch(
            photoProcessingCompleted({ photoId: asset.databaseId }),
          );
        }, 0);
        return mergeNewSubscriptionData(oldData, { asset, variables });
      },
    });
    return unsubscribe;
  }, [entityId, reduxDispatch, removeAssetFromWaiting, subscribeToMore]);

  return {
    loading,
    error,
    assets,
    waitingAssetCount: waitingAssets.length,
    parent,
    fetchNextPage,
    hasNextPage: pageInfoFromData(data, BASE_PATH).hasNextPage,
  };
}
